// Type2ContinueModal.jsx (Updated: Removed internal pagination; now uses ProcedureTable's filtered data. AI button with alert placeholder + loading)
import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVial,
  faTimes,
  faCheckCircle,
  faPlusCircle,
  faFilter,
  faExpand,
  faCompress,
  faBrain,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import axios from "axios";
import { BASE_URL } from "../../config"; // Adjust path as needed
import ProcedureTable from "./ProcedureTable"; // Aligned import
import { customStyles } from "./selectStyles"; // Your existing styles

// Reusable ActionButtons Sub-Component (Modernized AI Button)
const ActionButtons = ({
  onGenerate,
  onAnalyze,
  onClose,
  loading,
  selectedIcd,
  procedures,
  disabledGenerate = false,
  className = "",
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiPulse, setAiPulse] = useState(false);

  // AI pulse effect for attention
  useEffect(() => {
    if (!selectedIcd || procedures.length === 0) {
      const interval = setInterval(() => {
        setAiPulse((prev) => !prev);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [selectedIcd, procedures]);

  const handleAnalyze = async () => {
    if (!selectedIcd || procedures.length === 0) {
      alert(
        "Please select an ICD diagnosis and add at least one procedure to analyze."
      );
      return;
    }

    setIsAnalyzing(true);
    try {
      // Simulate loading (2s delay for placeholder)
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Placeholder alert (replace with real API later)
      const placeholderAnalysis = `AI Analysis Placeholder:\n\nICD: ${selectedIcd.label}\nProcedures: ${procedures.length} items\n\nAnalysis: The selected CPT procedures appear appropriate for this diagnosis. No mismatches detected. (This is a demo—integrate OpenAI for real validation.)`;
      alert(placeholderAnalysis);

      // Call parent's onAnalyze if provided for further handling
      if (onAnalyze) onAnalyze();
    } catch (error) {
      console.error("AI Analysis Error:", error);
      alert("Analysis failed. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className={`flex justify-end gap-1.5 ${className}`}>
      {/* Modern AI Analysis Button */}
      <motion.button
        whileHover={{
          scale: 1.05,
          boxShadow: "0 0 20px rgba(59, 130, 246, 0.5)",
        }}
        whileTap={{ scale: 0.95 }}
        className="relative px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl disabled:opacity-50 text-xs transition-all duration-300 flex items-center gap-2 overflow-hidden group"
        onClick={handleAnalyze}
        disabled={
          loading || isAnalyzing || !selectedIcd || procedures.length === 0
        }
        animate={{
          opacity:
            (!selectedIcd || procedures.length === 0) && aiPulse ? 0.8 : 1,
          scale:
            (!selectedIcd || procedures.length === 0) && aiPulse ? 1.02 : 1,
        }}
        transition={{ duration: 0.5 }}
      >
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute w-2 h-2 bg-white rounded-full opacity-20"
            animate={{
              x: [0, 100, 0],
              y: [0, -20, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: 0,
            }}
          />
          <motion.div
            className="absolute w-1 h-1 bg-cyan-300 rounded-full opacity-30"
            animate={{
              x: [50, 0, 50],
              y: [20, 0, 20],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              delay: 1,
            }}
          />
        </div>

        {/* Scanning beam effect */}
        {isAnalyzing && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{ x: [-100, 300] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        {/* Icon and text container */}
        <div className="relative z-10 flex items-center gap-2">
          {isAnalyzing ? (
            <>
              {/* AI Loading Animation */}
              <div className="relative">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                />
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div className="w-1 h-1 bg-cyan-300 rounded-full" />
                </motion.div>
              </div>
              <motion.span
                key="analyzing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="font-semibold"
              >
                AI analyzing...
              </motion.span>
            </>
          ) : (
            <>
              {/* AI Icon with Hover Effect */}
              <motion.div
                whileHover={{ scale: 1.2, rotate: 5 }}
                className="relative"
              >
                <FontAwesomeIcon icon={faBrain} className="text-sm" />
                {/* Neural connections dots */}
                <motion.div
                  className="absolute -top-1 -right-1 w-1 h-1 bg-cyan-300 rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
                <motion.div
                  className="absolute -bottom-1 -left-1 w-1 h-1 bg-purple-300 rounded-full"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.5 }}
                />
              </motion.div>
              <motion.span
                key="analyze"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="font-semibold"
              >
                Analyze with AI
              </motion.span>
            </>
          )}
        </div>

        {/* Success/Ready Indicator */}
        {!isAnalyzing && selectedIcd && procedures.length > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full"
          >
            <motion.div
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-full h-full bg-green-400 rounded-full"
            />
          </motion.div>
        )}
      </motion.button>

      {/* Other buttons remain the same */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="px-3 py-1.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 text-xs transition-colors"
        onClick={onGenerate}
        disabled={loading || disabledGenerate}
      >
        <FontAwesomeIcon icon={faCheckCircle} className="mr-1 text-xs" />
        Generate LOA
      </motion.button>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="px-3 py-1.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 text-xs transition-colors"
        onClick={onClose}
      >
        Close
      </motion.button>
    </div>
  );
};

const Type2ContinueModal = ({
  isOpen,
  onClose,
  selectedCoverage,
  selectedProvider,
  // icdData,
  selectedIcd,
  setSelectedIcd,
  selectedCpt,
  setSelectedCpt,
  cptRate,
  setCptRate,
  procedures,
  totalAmount,
  remarks,
  setRemarks,
  // searchIcdTerm,
  // setSearchIcdTerm,
  onAddProcedure,
  onRemoveProcedure,
  onEditRate,
  onType2Submit,
  loading,
}) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [shouldBump, setShouldBump] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const modalRef = useRef(null);

  // Fetch categories on mount
  useEffect(() => {
    if (isOpen) {
      const fetchCategories = async () => {
        try {
          setIsLoadingCategories(true);
          const response = await axios.get(
            `${BASE_URL}/generate-loa/cpt-categories`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            }
          );
          if (response.data.status === "success") {
            setCategories(response.data.data);
            setSelectedCategory(response.data.data[0]); // Default to 'ALL'
          }
        } catch (error) {
          console.error("Failed to fetch categories:", error);
          setCategories([{ value: "ALL", label: "All Categories" }]);
          setSelectedCategory(categories[0]);
        } finally {
          setIsLoadingCategories(false);
        }
      };
      fetchCategories();
    }
  }, [isOpen]);

  // Debounced load for CPT options
  const loadCptOptions = useCallback(
    async (inputValue, callback) => {
      if (!inputValue || inputValue.length < 2) {
        callback([]);
        return;
      }
      try {
        const params = {
          q: inputValue,
          category: selectedCategory?.value || "ALL",
          provider_code: selectedProvider?.id || "",
        };
        const response = await axios.get(`${BASE_URL}/generate-loa/cpts`, {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        });
        if (response.data.status === "success") {
          callback(response.data.data);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error("Failed to load CPTs:", error);
        callback([]);
      }
    },
    [selectedCategory, selectedProvider]
  );

  // Handle outside clicks (bump animation)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShouldBump(true);
        setTimeout(() => setShouldBump(false), 300);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Escape key close
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isOpen, onClose]);

  const loadIcdOptions = useCallback(async (inputValue, callback) => {
    if (!inputValue || inputValue.length < 2) {
      callback([]);
      return;
    }
    try {
      const response = await axios.get(`${BASE_URL}/generate-loa/icds`, {
        params: { q: inputValue },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      if (response.data.status === "success") {
        callback(response.data.data);
      } else {
        callback([]);
      }
    } catch (error) {
      console.error("Failed to load ICDs:", error);
      callback([]);
    }
  }, []);

  const handleIcdSelect = (selected) => setSelectedIcd(selected);

  const handleCategoryChange = (selected) => {
    setSelectedCategory(selected);
    setSelectedCpt(null); // Reset CPT on category change
    setCptRate(0);
  };

  const handleCptSelect = (selected) => {
    setSelectedCpt(selected);
    if (selected) {
      setCptRate(selected.rate || 0); // Pre-resolved effective_rate
    }
  };

  const handleAdd = () => {
    if (selectedCpt && cptRate > 0) {
      onAddProcedure({ cpt: selectedCpt, rate: cptRate });
      setSelectedCpt(null);
      setCptRate(0);
    }
  };

  const toggleMaximize = () => {
    setIsMaximized((current) => !current);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-y-0 left-0 tracker-aware-modal-layer bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 ${
          isMaximized ? "p-0 sm:p-2" : "p-2 sm:p-4"
        }`}
      >
        <motion.div
          ref={modalRef}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{
            scale: shouldBump ? [1, 1.02, 1] : 1,
            opacity: 1,
          }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{
            duration: shouldBump ? 0.3 : 0.2,
            ease: shouldBump ? "easeInOut" : "easeOut",
          }}
          className={`bg-white w-full shadow-2xl border border-gray-100 transition-all duration-200 flex flex-col overflow-hidden ${
            isMaximized
              ? "h-full max-h-full max-w-none rounded-none p-3 sm:rounded-xl sm:p-4"
              : "max-w-5xl max-h-[90vh] rounded-2xl p-3 sm:p-4"
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-1xl text-gray-900 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faVial} className="text-indigo-600" />
              Letter of Authorization for{" "}
              {selectedCoverage?.text || "Diagnostic/Lab"}
            </h2>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleMaximize}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                <FontAwesomeIcon
                  icon={isMaximized ? faCompress : faExpand}
                  className="text-lg"
                />
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <FontAwesomeIcon icon={faTimes} className="text-lg" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-1 min-h-0 justify-center overflow-y-auto py-4">
              <FontAwesomeIcon
                icon={faVial}
                spin
                size="lg"
                className="text-indigo-500"
              />
              Please wait ...
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto pr-1">
              <div className="space-y-3">
              {/* Diagnosis ICD */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-700 flex items-center gap-1">
                  (ICD Codes) Diagnosis <span className="text-red-500">*</span>
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-xs text-gray-400"
                  />
                </label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions // Load initial on focus
                  loadOptions={loadIcdOptions}
                  value={selectedIcd}
                  onChange={handleIcdSelect}
                  placeholder="Type to search ICD (min 2 chars)..."
                  loadingMessage={() => "Searching ICDs..."}
                  noOptionsMessage={() => "Type to search ICDs"}
                  styles={customStyles}
                  className="text-xs"
                  debounceTimeout={300}
                />
              </div>

              {/* Category Filter + Procedure CPT + Rate + Add */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-700">
                  Filter by Category
                </label>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  options={categories}
                  isLoading={isLoadingCategories}
                  placeholder="Select category..."
                  styles={customStyles}
                  className="text-xs"
                  isSearchable={false}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                      (CPT Rates) Procedure{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadCptOptions}
                      value={selectedCpt}
                      onChange={handleCptSelect}
                      placeholder="Type to search CPT (min 2 chars)..."
                      loadingMessage={() => "Searching CPTs..."}
                      noOptionsMessage={() => "Type to search CPTs"}
                      styles={customStyles}
                      className="text-xs"
                      debounceTimeout={300}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                      Rate (₱)
                    </label>
                    <input
                      type="number"
                      value={cptRate}
                      onChange={(e) =>
                        setCptRate(parseFloat(e.target.value) || 0)
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-xs transition"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAdd}
                    disabled={!selectedCpt || cptRate <= 0}
                    className="px-2.5 py-1.5 bg-green-600 text-white rounded-md disabled:opacity-50 flex items-center gap-1 justify-center md:col-span-3 text-xs font-medium transition-colors hover:bg-green-700"
                  >
                    <FontAwesomeIcon icon={faPlusCircle} />
                    Add
                  </motion.button>
                </div>
              </div>

              {/* Procedures Table (Now handles its own filtering; no modal pagination) */}
              {procedures.length > 0 && (
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-gray-700">
                    Added Procedures
                  </label>
                  <ProcedureTable
                    procedures={procedures} // Pass full; table filters internally
                    onRemove={onRemoveProcedure}
                    onEditRate={onEditRate}
                    providerId={selectedProvider.id}
                  />
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      Total: ₱{totalAmount.toFixed(2)}
                    </p>
                  </div>
                </div>
              )}

              {/* Remarks */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-700">
                  Remarks
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 20000) setRemarks(value);
                  }}
                  placeholder="Enter remarks (max 20000 chars)"
                  rows={4.5}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-xs transition"
                />
                <div className="text-right text-xs text-gray-500">
                  {remarks.length}/20000 chars
                </div>
              </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="sticky bottom-0 z-20 flex-shrink-0 border-t border-gray-200 bg-white pt-3 mt-3 shadow-[0_-8px_16px_rgba(15,23,42,0.06)]">
            <ActionButtons
              onGenerate={onType2Submit}
              onAnalyze={() => {}} // Optional parent handler
              onClose={onClose}
              loading={loading}
              selectedIcd={selectedIcd}
              procedures={procedures}
              disabledGenerate={!selectedIcd || procedures.length === 0}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Type2ContinueModal;
